import type { ChartDatapoint } from '../../../core/types/chart-data.js';
import { makeAccessor } from '../../../core/utils/makeAccessor.js';
import { FLAME_ORIGINAL_IDX } from '../constants.js';

type BuildFlameIndexAccessors = {
  idAccessor: string;
  parentIdAccessor: string;
};

/**
 * Precomputed lookups over the caller's raw span data. All three views share
 * the same cloned datum references so downstream code can round-trip a span
 * across them without paying for another clone.
 * @internal
 */
export type FlameIndex<TData extends ChartDatapoint> = {
  /**
   * Clones of the input spans with `FLAME_ORIGINAL_IDX` attached so tooltips can round-trip to the caller's array.
   */
  indexedData: TData[];
  /**
   * Span id → indexed datum. `O(1)` lookup for the isolation root.
   */
  byId: Map<unknown, TData>;
  /**
   * Parent id → indexed children. Populated only for spans whose parent id is defined.
   */
  childrenByParentId: Map<unknown, TData[]>;
};

/**
 * Walks `data` exactly once and materializes every lookup FlameChart /
 * FlameGraph need to render or isolate a subtree. Consolidates work that used
 * to happen across the `dataWithIndex` map, `getDescendants`'s internal
 * parent-map build, and the ad-hoc `.find(...)` used to locate the isolation
 * root — three separate `O(n)` passes fused into one.
 *
 * At one-million-span scale this single pass replaces three, and callers
 * downstream (`collectSubtree`) reuse the maps instead of rebuilding them.
 * @internal
 */
export function buildFlameIndex<TData extends ChartDatapoint>(
  data: readonly TData[],
  { idAccessor, parentIdAccessor }: BuildFlameIndexAccessors,
): FlameIndex<TData> {
  const readId = makeAccessor(idAccessor);
  const readParentId = makeAccessor(parentIdAccessor);

  const n = data.length;
  const indexedData = new Array<TData>(n);
  const byId = new Map<unknown, TData>();
  const childrenByParentId = new Map<unknown, TData[]>();

  for (let i = 0; i < n; i++) {
    const indexed = { ...data[i], [FLAME_ORIGINAL_IDX]: i } as TData;
    indexedData[i] = indexed;
    byId.set(readId(indexed), indexed);

    const parentId = readParentId(indexed);
    if (parentId === null || parentId === undefined) {
      continue;
    }
    const siblings = childrenByParentId.get(parentId);
    if (siblings) {
      siblings.push(indexed);
    } else {
      childrenByParentId.set(parentId, [indexed]);
    }
  }

  return { indexedData, byId, childrenByParentId };
}
